import React, { useState } from 'react';

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import FormControlLabel from '@mui/material/FormControlLabel';
import LinearProgress from '@mui/material/LinearProgress';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';

import { UserSession } from 'stores/AuthenticationStoreAdapter';
import {
  SecurityCheckDetailResult,
  SecurityCheckPcResultItem,
  remindSecurityCheck,
  startSecurityCheck,
} from 'stores/SecurityCheckStoreAdapter';
import { formatDateTime } from 'utils/DateTimeFormatter';
import { errorDefault, successDefault } from 'utils/Toastify';

type Props = {
  financialYear: number;
  month: number;
  detail: SecurityCheckDetailResult;
  userSession: UserSession;
  onRefresh: () => void;
};

type TabValue = 'monthly' | 'pcPatch';

/** 資産詳細を別タブで開く */
const openAssessmentDetail = (assessmentId: string) => {
  window.open(`/assessment/assessment-management/${assessmentId}`, '_blank', 'noopener,noreferrer');
};

/** セクション見出し（未実施 / 実施済み）。ツリーではなくフラットなヘッダー。 */
const SectionLabel: React.FC<{ label: string; count: number; tone: 'pending' | 'done' }> = ({
  label,
  count,
  tone,
}) => (
  <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 1 }}>
    {tone === 'pending' ? (
      <RadioButtonUncheckedIcon sx={{ fontSize: 16, color: 'warning.main' }} />
    ) : (
      <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />
    )}
    <Typography variant="caption" fontWeight={700} color="text.secondary">
      {label}
    </Typography>
    <Chip
      size="small"
      label={count}
      sx={{ height: 18, fontSize: 11, fontWeight: 700, borderRadius: 1 }}
    />
  </Stack>
);

const SecurityCheckAdminDetail: React.FC<Props> = ({
  financialYear,
  month,
  detail,
  userSession,
  onRefresh,
}) => {
  const [activeTab, setActiveTab] = useState<TabValue>('monthly');
  const [checkedUsers, setCheckedUsers] = useState<Set<string>>(new Set());
  const [groupByUser, setGroupByUser] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const isNotStarted =
    detail.monthly.status === 'NOT_STARTED' && detail.pcPatch.status === 'NOT_STARTED';

  const canManage =
    userSession.roles.includes('ADMIN') ||
    userSession.roles.includes('BACK_OFFICE') ||
    userSession.roles.includes('MANAGER');

  const handleStart = () => {
    setSubmitting(true);
    startSecurityCheck({ financialYear, month })
      .then(() => {
        successDefault('セキュリティチェックを開始しました');
        onRefresh();
      })
      .catch((e: Error) => errorDefault('セキュリティチェックの開始に失敗しました', e.message))
      .finally(() => setSubmitting(false));
  };

  const handleRemind = () => {
    if (checkedUsers.size === 0) {
      errorDefault('リマインド対象を選択してください');
      return;
    }
    setSubmitting(true);
    const type = activeTab === 'monthly' ? 'MONTHLY' : 'PC_PATCH';
    remindSecurityCheck({
      securityCheckType: type as 'MONTHLY' | 'PC_PATCH',
      financialYear,
      month,
      userIds: Array.from(checkedUsers),
    })
      .then(() => {
        successDefault('リマインドを送信しました');
        setCheckedUsers(new Set());
      })
      .catch((e: Error) => errorDefault('リマインドの送信に失敗しました', e.message))
      .finally(() => setSubmitting(false));
  };

  const toggleUser = (userId: string) => {
    setCheckedUsers((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  };

  // --- 未実施状態: 開始ボタンのみ ---
  if (isNotStarted) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>
          {financialYear}期 {month}月度 セキュリティチェック
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          この月度のチェックはまだ開始されていません。
        </Typography>
        {canManage && (
          <Button
            variant="contained"
            startIcon={<PlayArrowIcon />}
            onClick={handleStart}
            disabled={submitting}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              boxShadow: 'none',
              '&:hover': { boxShadow: 'none' },
            }}
          >
            セキュリティチェック開始
          </Button>
        )}
      </Box>
    );
  }

  // --- 実施中/完了状態 ---
  const uncompletedMonthly = detail.monthly.results.filter((r) => r.resultCode === null);
  const completedMonthly = detail.monthly.results.filter((r) => r.resultCode !== null);
  const uncompletedPc = detail.pcPatch.results.filter((r) => r.resultCode === null);
  const completedPc = detail.pcPatch.results.filter((r) => r.resultCode !== null);

  const monthlyTotal = detail.monthly.results.length;
  const pcTotal = detail.pcPatch.results.length;
  const monthlyDone = completedMonthly.length;
  const pcDone = completedPc.length;

  const activeTotal = activeTab === 'monthly' ? monthlyTotal : pcTotal;
  const activeDone = activeTab === 'monthly' ? monthlyDone : pcDone;
  const activePercent = activeTotal === 0 ? 0 : Math.round((activeDone / activeTotal) * 100);

  // 未実施の一括チェック（対象ユーザーIDの集合）
  const pendingUserIds =
    activeTab === 'monthly'
      ? uncompletedMonthly.map((r) => r.userId)
      : Array.from(new Set(uncompletedPc.map((r) => r.userId)));
  const allPendingChecked =
    pendingUserIds.length > 0 && pendingUserIds.every((id) => checkedUsers.has(id));
  const somePendingChecked = checkedUsers.size > 0 && !allPendingChecked;
  const toggleAllPending = () => {
    if (allPendingChecked) setCheckedUsers(new Set());
    else setCheckedUsers(new Set(pendingUserIds));
  };

  return (
    <Box sx={{ p: 2.5, height: '100%', overflow: 'auto' }}>
      {/* ヘッダー */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1}
        sx={{ mb: 2 }}
      >
        <Typography variant="subtitle1" fontWeight={700}>
          {financialYear}期 {month}月度
        </Typography>
        {canManage && (
          <Button
            variant="contained"
            size="small"
            startIcon={<NotificationsActiveIcon />}
            onClick={handleRemind}
            disabled={submitting || checkedUsers.size === 0}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              boxShadow: 'none',
              '&:hover': { boxShadow: 'none' },
            }}
          >
            リマインド{checkedUsers.size > 0 ? `（${checkedUsers.size}）` : ''}
          </Button>
        )}
      </Stack>

      {/* 進捗サマリー（2種別） */}
      <Stack direction="row" spacing={1.5} sx={{ mb: 2 }}>
        <ProgressCard label="月次チェック" done={monthlyDone} total={monthlyTotal} />
        <ProgressCard label="PCパッチ適用" done={pcDone} total={pcTotal} />
      </Stack>

      {/* タブ */}
      <Tabs
        value={activeTab}
        onChange={(_, v) => {
          setActiveTab(v);
          setCheckedUsers(new Set());
        }}
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          minHeight: 38,
          mb: 2,
          '& .MuiTab-root': {
            minHeight: 38,
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.82rem',
          },
        }}
      >
        <Tab value="monthly" label={`月次チェック（${monthlyDone}/${monthlyTotal}）`} />
        <Tab value="pcPatch" label={`PCパッチ確認（${pcDone}/${pcTotal}）`} />
      </Tabs>

      {/* アクティブタブの進捗バー */}
      <LinearProgress
        variant="determinate"
        value={activePercent}
        color={activePercent === 100 ? 'success' : 'primary'}
        sx={{ height: 6, borderRadius: 3, mb: 2 }}
      />

      {/* PCパッチ: ユーザーごとにまとめる切替 */}
      {activeTab === 'pcPatch' && (
        <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1 }}>
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={groupByUser}
                onChange={(e) => setGroupByUser(e.target.checked)}
              />
            }
            label={<Typography variant="caption">ユーザーごとにまとめる</Typography>}
          />
        </Stack>
      )}

      {/* 未実施セクション */}
      {pendingUserIds.length > 0 && (
        <Box sx={{ mb: 2.5 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
            <SectionLabel label="未実施" count={pendingUserIds.length} tone="pending" />
            {canManage && (
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={allPendingChecked}
                    indeterminate={somePendingChecked}
                    onChange={toggleAllPending}
                  />
                }
                label={<Typography variant="caption">全選択</Typography>}
                sx={{ mr: 0 }}
              />
            )}
          </Stack>

          {activeTab === 'monthly' ? (
            <Stack spacing={0.5}>
              {uncompletedMonthly.map((r) => (
                <PendingRow
                  key={r.securityCheckResultId}
                  checked={checkedUsers.has(r.userId)}
                  onToggle={() => toggleUser(r.userId)}
                  canManage={canManage}
                  primary={r.userName}
                />
              ))}
            </Stack>
          ) : groupByUser ? (
            <PcGroupedByUser
              items={uncompletedPc}
              checkedUsers={checkedUsers}
              toggleUser={toggleUser}
              canManage={canManage}
            />
          ) : (
            <Stack spacing={0.5}>
              {uncompletedPc.map((r) => (
                <PendingRow
                  key={r.securityCheckResultId}
                  checked={checkedUsers.has(r.userId)}
                  onToggle={() => toggleUser(r.userId)}
                  canManage={canManage}
                  primary={`${r.assessmentName}${r.managementNumber1 ? ` #${r.managementNumber1}` : ''}`}
                  secondary={r.userName}
                  onOpen={() => openAssessmentDetail(r.assessmentId)}
                />
              ))}
            </Stack>
          )}
        </Box>
      )}

      {/* 実施済みセクション */}
      {(activeTab === 'monthly' ? completedMonthly.length : completedPc.length) > 0 && (
        <Box>
          <SectionLabel
            label="実施済み"
            count={activeTab === 'monthly' ? completedMonthly.length : completedPc.length}
            tone="done"
          />
          <Stack spacing={0.5}>
            {activeTab === 'monthly'
              ? completedMonthly.map((r) => (
                  <DoneRow key={r.securityCheckResultId} primary={r.userName} at={r.completedAt} />
                ))
              : completedPc.map((r) => (
                  <DoneRow
                    key={r.securityCheckResultId}
                    primary={`${r.assessmentName}${r.managementNumber1 ? ` #${r.managementNumber1}` : ''}`}
                    secondary={r.userName}
                    at={r.completedAt}
                    onOpen={() => openAssessmentDetail(r.assessmentId)}
                  />
                ))}
          </Stack>
        </Box>
      )}
    </Box>
  );
};

// --- 進捗カード（種別ごと） ---
const ProgressCard: React.FC<{ label: string; done: number; total: number }> = ({
  label,
  done,
  total,
}) => {
  const percent = total === 0 ? 0 : Math.round((done / total) * 100);
  const complete = total > 0 && done === total;
  return (
    <Box
      sx={{
        flex: 1,
        border: 1,
        borderColor: 'divider',
        borderRadius: 2,
        p: 1.5,
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.75 }}>
        <Typography variant="caption" color="text.secondary" fontWeight={600}>
          {label}
        </Typography>
        <Typography
          variant="caption"
          fontWeight={700}
          sx={{ color: complete ? 'success.main' : 'text.primary' }}
        >
          {done} / {total}
        </Typography>
      </Stack>
      <LinearProgress
        variant="determinate"
        value={percent}
        color={complete ? 'success' : 'primary'}
        sx={{ height: 5, borderRadius: 3 }}
      />
    </Box>
  );
};

// --- 未実施行（選択可） ---
const PendingRow: React.FC<{
  checked: boolean;
  onToggle: () => void;
  canManage: boolean;
  primary: string;
  secondary?: string;
  onOpen?: () => void;
}> = ({ checked, onToggle, canManage, primary, secondary, onOpen }) => (
  <Stack
    direction="row"
    alignItems="center"
    spacing={1}
    sx={{
      px: 1,
      py: 0.5,
      borderRadius: 1.5,
      border: 1,
      borderColor: checked ? 'primary.main' : 'divider',
      bgcolor: checked ? 'action.selected' : 'transparent',
      transition: 'border-color 0.15s, background-color 0.15s',
      '&:hover': { bgcolor: 'action.hover' },
    }}
  >
    {canManage && <Checkbox checked={checked} size="small" sx={{ p: 0.25 }} onChange={onToggle} />}
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography variant="body2" noWrap>
        {primary}
      </Typography>
      {secondary && (
        <Typography variant="caption" color="text.secondary" noWrap component="div">
          {secondary}
        </Typography>
      )}
    </Box>
    {onOpen && (
      <OpenInNewIcon
        sx={{ fontSize: 15, color: 'text.disabled', cursor: 'pointer', flexShrink: 0 }}
        onClick={onOpen}
      />
    )}
  </Stack>
);

// --- 実施済み行 ---
const DoneRow: React.FC<{
  primary: string;
  secondary?: string;
  at: string | null;
  onOpen?: () => void;
}> = ({ primary, secondary, at, onOpen }) => (
  <Stack
    direction="row"
    alignItems="center"
    spacing={1}
    sx={{ px: 1, py: 0.5, borderRadius: 1.5, '&:hover': { bgcolor: 'action.hover' } }}
  >
    <CheckCircleIcon sx={{ fontSize: 15, color: 'success.main', flexShrink: 0 }} />
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography variant="body2" noWrap>
        {primary}
      </Typography>
      {secondary && (
        <Typography variant="caption" color="text.secondary" noWrap component="div">
          {secondary}
        </Typography>
      )}
    </Box>
    <Typography variant="caption" color="text.disabled" sx={{ flexShrink: 0 }}>
      {at ? formatDateTime(at) : ''}
    </Typography>
    {onOpen && (
      <OpenInNewIcon
        sx={{ fontSize: 15, color: 'text.disabled', cursor: 'pointer', flexShrink: 0 }}
        onClick={onOpen}
      />
    )}
  </Stack>
);

// --- PCパッチ: ユーザーごとにまとめた未実施表示 ---
const PcGroupedByUser: React.FC<{
  items: SecurityCheckPcResultItem[];
  checkedUsers: Set<string>;
  toggleUser: (userId: string) => void;
  canManage: boolean;
}> = ({ items, checkedUsers, toggleUser, canManage }) => {
  const byUser = new Map<string, SecurityCheckPcResultItem[]>();
  items.forEach((r) => {
    const list = byUser.get(r.userId) || [];
    list.push(r);
    byUser.set(r.userId, list);
  });

  return (
    <Stack spacing={1}>
      {Array.from(byUser.entries()).map(([userId, list]) => (
        <Box
          key={userId}
          sx={{
            border: 1,
            borderColor: checkedUsers.has(userId) ? 'primary.main' : 'divider',
            borderRadius: 1.5,
            bgcolor: checkedUsers.has(userId) ? 'action.selected' : 'transparent',
            overflow: 'hidden',
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 1, py: 0.5 }}>
            {canManage && (
              <Checkbox
                checked={checkedUsers.has(userId)}
                size="small"
                sx={{ p: 0.25 }}
                onChange={() => toggleUser(userId)}
              />
            )}
            <Typography variant="body2" fontWeight={600}>
              {list[0].userName}
            </Typography>
            <Chip
              size="small"
              label={`${list.length}件`}
              sx={{ height: 18, fontSize: 10, borderRadius: 1 }}
            />
          </Stack>
          <Stack spacing={0} sx={{ px: 1.5, pb: 0.75 }}>
            {list.map((r) => (
              <Stack
                key={r.securityCheckResultId}
                direction="row"
                alignItems="center"
                spacing={0.5}
                sx={{ py: 0.25, cursor: 'pointer' }}
                onClick={() => openAssessmentDetail(r.assessmentId)}
              >
                <Typography variant="caption" color="text.secondary" noWrap sx={{ flex: 1 }}>
                  {r.assessmentName}
                  {r.managementNumber1 ? ` #${r.managementNumber1}` : ''}
                </Typography>
                <OpenInNewIcon sx={{ fontSize: 13, color: 'text.disabled' }} />
              </Stack>
            ))}
          </Stack>
        </Box>
      ))}
    </Stack>
  );
};

export default SecurityCheckAdminDetail;
